/**
 * useEditGroupForm — web counterpart of mobile's EditGroup screen: seeds name,
 * description, privacy and images from the group's current details and saves
 * through the shared groupService.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import groupService from '@wlb/shared/api/services/groupService';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_PRIVACY, GROUP_STATUS } from '@wlb/shared/constants';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types/api';
import { resolveGroupImageUrl } from './groupMedia';
import { isGroupAdmin } from './groupPermissions';
import { useGroupDetails } from './useGroupDetails';
import { useGroupImagePicker, type GroupImageSlot } from './useGroupImagePicker';

interface EditGroupErrors {
  groupName?: string;
  description?: string;
  privacy?: string;
}

export interface UseEditGroupFormResult {
  /** True while the group's current values are still being fetched. */
  loading: boolean;
  loadError: string;
  reload: () => void;
  /** Null until the group loads, then whether this member may edit it. */
  canEdit: boolean | null;
  groupName: string;
  description: string;
  privacy: string;
  /** Newly picked image if there is one, otherwise the group's current image. */
  coverPreview: string | null;
  avatarPreview: string | null;
  errors: EditGroupErrors;
  saving: boolean;
  setGroupName: (value: string) => void;
  setDescription: (value: string) => void;
  setPrivacy: (value: string) => void;
  setImage: (slot: GroupImageSlot, file: File) => void;
  submit: () => Promise<void>;
  cancel: () => void;
}

export function useEditGroupForm(groupId?: string): UseEditGroupFormResult {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateGroupInState } = useGroup();
  const { detail, loading, error: loadError, reload } = useGroupDetails(groupId);
  const { coverImage, avatarImage, setImage } = useGroupImagePicker();

  const [groupName, setGroupNameValue] = useState('');
  const [description, setDescriptionValue] = useState('');
  const [privacy, setPrivacyValue] = useState<string>(GROUP_PRIVACY.PUBLIC);
  const [errors, setErrors] = useState<EditGroupErrors>({});
  const [saving, setSaving] = useState(false);
  // Seeding is keyed by group so switching groups refills the form, while a
  // reload of the same group never overwrites edits the member has typed.
  const seededGroupId = useRef<string | null>(null);

  useEffect(() => {
    if (!detail || seededGroupId.current === detail.data.id) return;
    seededGroupId.current = detail.data.id;
    const group = detail.data;
    setGroupNameValue(group.groupName ?? '');
    setDescriptionValue(group.groupDescription ?? '');
    setPrivacyValue(
      String(group.privacyType).toLowerCase() === GROUP_PRIVACY.PRIVATE.toLowerCase()
        ? GROUP_PRIVACY.PRIVATE
        : GROUP_PRIVACY.PUBLIC,
    );
  }, [detail]);

  const setGroupName = useCallback((value: string) => {
    setGroupNameValue(value);
    setErrors(prev => (prev.groupName ? { ...prev, groupName: undefined } : prev));
  }, []);

  const setDescription = useCallback((value: string) => {
    setDescriptionValue(value);
    setErrors(prev => (prev.description ? { ...prev, description: undefined } : prev));
  }, []);

  const setPrivacy = useCallback((value: string) => {
    setPrivacyValue(value);
    setErrors(prev => (prev.privacy ? { ...prev, privacy: undefined } : prev));
  }, []);

  const cancel = useCallback(() => {
    navigate(groupId ? `/groups/${groupId}` : '/groups/my');
  }, [groupId, navigate]);

  const submit = useCallback(async () => {
    if (!groupId || !detail) return;

    const nextErrors: EditGroupErrors = {};
    if (!groupName.trim()) nextErrors.groupName = t('groupDetails.editGroup.nameEmpty');
    if (!description.trim()) {
      nextErrors.description = t('groupDetails.editGroup.descriptionEmpty');
    }
    if (!privacy) nextErrors.privacy = t('groupDetails.editGroup.privacyRequired');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      const response = await groupService.updateGroup(groupId, {
        groupName: groupName.trim(),
        groupDescription: description.trim(),
        privacyType: privacy.toLowerCase(),
        requireApproval: privacy === GROUP_PRIVACY.PRIVATE,
        // UpdateGroup rewrites both posting flags on every call, so send the
        // group's current values back or editing the name would silently
        // reopen posting to everyone.
        requirePostApproval: detail.meta.requirePostApproval,
        onlyAdminCanPost: detail.meta.onlyAdminCanPost,
        groupImage: avatarImage?.file ?? null,
        groupCoverImage: coverImage?.file ?? null,
      });

      if (!response?.success) {
        throw new Error('Update group failed');
      }

      updateGroupInState({
        id: groupId,
        groupName: response.data?.groupName || groupName.trim(),
        groupDescription: response.data?.groupDescription || description.trim(),
        privacyType: response.data?.privacyType || privacy.toLowerCase(),
        // Only server URLs are safe here: a picked file's preview is an object
        // URL that this hook revokes on unmount.
        groupImage: response.data?.groupImage,
        groupCoverImage: response.data?.groupCoverImage,
        // The update response is built by mapApiGroupRecordToGroup with
        // hardcoded status='joined' and totalMembers=1, which would corrupt
        // the owner's real status/count in the shared list state. The edit
        // operation changes neither, so preserve the values from the detail
        // fetch instead of trusting the incomplete update response.
        totalMembers: detail.data.totalMembers,
        status: detail.data.status || GROUP_STATUS.OWNER,
      });
      navigate(`/groups/${groupId}`);
    } catch (err) {
      // getApiErrorMessage assumes an Axios-style error with a .response.
      // A plain Error (thrown when the API returns success: false) has no
      // .response, so getApiErrorMessage would return a generic "network
      // error" string and the localized fallback would never be shown.
      const message =
        err && typeof err === 'object' && 'response' in err
          ? getApiErrorMessage(err as ApiError)
          : t('groupDetails.editGroup.updateFailed');
      ToastService.show({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  }, [
    groupId,
    detail,
    groupName,
    description,
    privacy,
    avatarImage,
    coverImage,
    updateGroupInState,
    navigate,
    t,
  ]);

  return {
    loading,
    loadError,
    reload,
    canEdit: detail ? isGroupAdmin(detail) : null,
    groupName,
    description,
    privacy,
    coverPreview: coverImage?.previewUrl ?? resolveGroupImageUrl(detail?.data.groupCoverImage),
    avatarPreview: avatarImage?.previewUrl ?? resolveGroupImageUrl(detail?.data.groupProfileImage),
    errors,
    saving,
    setGroupName,
    setDescription,
    setPrivacy,
    setImage,
    submit,
    cancel,
  };
}
